import React, { useEffect, useState } from 'react';
import { NavDropdown, Container } from 'react-bootstrap';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaChevronDown, FaTimes } from 'react-icons/fa';
import { auth } from '../../firebase/firebase-config';
import { signOut } from 'firebase/auth';
import { useTenant } from '../../context/TenantContext';
import { useUser } from '../../context/UserContext';
import { useMembership } from '../../context/MembershipContext';
import {
  getEnabledDropdownItems,
  getEnabledNavItems,
} from '../../platform/moduleNavigation';
import { canManageTenantForTenant } from '../../platform/membershipPermissions';
import { resolveProfileDisplayName } from '../../platform/displayName';
import {
  resolveFooterCopyright,
  resolveFooterLinks,
} from '../../platform/appIdentity';
import ProfileAvatar from '../common/ProfileAvatar';
import NotificationBell from '../notifications/NotificationBell';
import './Dashboard.css';

function navLinkClassName({ isActive }) {
  return isActive ? 'app-navbar__link app-navbar__link--active' : 'app-navbar__link';
}

function Dashboard() {
  const { config, tenantId, clearSelectedOrganization } = useTenant();
  const { user } = useUser();
  const { memberships } = useMembership();
  const { branding } = config;
  const footerCopyright = resolveFooterCopyright(config);
  const footerLinks = resolveFooterLinks(config);
  const navItems = getEnabledNavItems(config);
  const dropdownItems = getEnabledDropdownItems(config, {
    canManageTenant: canManageTenantForTenant(memberships, tenantId, user),
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const profileLabel = user?.displayName || user?.email || 'Profil';
  const profileInitialsName = resolveProfileDisplayName(user) || profileLabel;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleNavClick = (path) => (event) => {
    const fullPath = `/${path}`;
    if (location.pathname === fullPath) {
      event.preventDefault();
      navigate(0);
    }
  };

  const handleLogout = async () => {
    try {
      clearSelectedOrganization();
      await signOut(auth);
      navigate('/select-organization');
    } catch (error) {
      console.error('Logout Error:', error);
    }
  };

  return (
    <div className="dashboard-container">
      <header className="app-navbar">
        <Container fluid="lg" className="app-navbar__bar">
          <div className="app-layout app-layout--bar">
            <NavLink to="/" className="app-layout__brand app-navbar__brand">
              <img
                src={branding.logoPath}
                alt={branding.logoAlt || config.displayName}
                className="app-navbar__logo"
              />
              <span className="app-navbar__title">{branding.appTitle}</span>
            </NavLink>

            <div className="app-layout__workspace app-layout__workspace--header">
              <nav className="app-navbar__links" aria-label="Glavna navigacija">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={`/${item.path}`}
                    className={navLinkClassName}
                    onClick={handleNavClick(item.path)}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div className="app-navbar__bell">
                <NotificationBell />
              </div>
            </div>

            <div className="app-layout__aside app-navbar__aside">
              <NavDropdown
                align="end"
                className="app-navbar__profile-menu"
                title={(
                  <span className="app-navbar__profile-trigger">
                    <ProfileAvatar
                      uid={user?.uid}
                      photoURL={user?.photoURL}
                      displayName={profileInitialsName}
                      authorColor={user?.chatColor}
                      className="app-navbar__avatar"
                      photoClassName="app-navbar__avatar"
                      initialsClassName="app-navbar__avatar app-navbar__avatar--initials"
                    />
                    <span className="app-navbar__profile-name">{profileLabel}</span>
                    <FaChevronDown className="app-navbar__profile-chevron" aria-hidden="true" />
                  </span>
                )}
                id="app-navbar-profile-menu"
              >
                {dropdownItems.map((item) => (
                  <NavDropdown.Item key={item.path} as={NavLink} to={`/${item.path}`}>
                    {item.label}
                  </NavDropdown.Item>
                ))}
                <NavDropdown.Divider />
                <NavDropdown.Item onClick={handleLogout}>Odjava</NavDropdown.Item>
              </NavDropdown>

              <button
                type="button"
                className="app-navbar__menu-btn"
                aria-label={mobileOpen ? 'Zapri meni' : 'Odpri meni'}
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen((open) => !open)}
              >
                {mobileOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </Container>

        {mobileOpen && (
          <nav className="app-navbar__mobile" aria-label="Mobilna navigacija">
            <Container fluid="lg" className="app-navbar__mobile-inner">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={`/${item.path}`}
                  className={navLinkClassName}
                  onClick={handleNavClick(item.path)}
                >
                  {item.label}
                </NavLink>
              ))}
            </Container>
          </nav>
        )}
      </header>

      <main className="dashboard-main">
        <Container fluid="lg">
          <div className="app-layout app-layout--page">
            <div className="app-layout__brand-spacer" aria-hidden="true" />
            <div className="app-layout__workspace">
              <Outlet />
            </div>
            <div className="app-layout__aside-spacer" aria-hidden="true" />
          </div>
        </Container>
      </main>

      <footer className="site-footer">
        <Container>
          <div className="site-footer__inner">
            <p className="site-footer__copyright">
              © {new Date().getFullYear()} {footerCopyright}
            </p>
            {footerLinks.length > 0 && (
              <nav className="site-footer__links" aria-label="Pravne povezave">
                {footerLinks.map((link, index) => (
                  <React.Fragment key={link.href || link.label}>
                    {index > 0 && (
                      <span className="site-footer__sep" aria-hidden="true">
                        ·
                      </span>
                    )}
                    <a href={link.href} target="_blank" rel="noopener noreferrer">
                      {link.label}
                    </a>
                  </React.Fragment>
                ))}
              </nav>
            )}
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default Dashboard;
